import { FunctionComponent, useCallback, useEffect, useRef } from 'react';
import { Box, Typography, Theme, Link } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import oxTheOx from '../../assets/images/ox-the-ox.svg';
import { LoadingButton } from '@mui/lab';
import { sendEmailVerification } from '../actions/email-verification-actions';
import { useSnapshot } from 'valtio';
import EmailVerificationStore from '../stores/email-verification-store';
import { useAuth0 } from '@auth0/auth0-react';
import { AppPages, navigate } from '../../common/routing/app-navigator';
import { submitHubSpotForm } from '../../hubspot-analytics/actions/hubspot-analytics-actions';
import { HubSpotFormsIds } from '../../hubspot-analytics/hubspot-types';

const EmailVerificationContainer: FunctionComponent<
  React.PropsWithChildren<unknown>
> = () => {
  const { classes } = useStyles();
  const { isLoading, emailVerificationError } = useSnapshot(
    EmailVerificationStore,
  );
  const { user } = useAuth0();
  const firstMount = useRef(true);

  const onClick = useCallback(() => {
    sendEmailVerification();
  }, []);

  useEffect(() => {
    if (user && user.email_verified) {
      navigate(AppPages.Home);
    } else if (user && user.email && firstMount.current) {
      submitHubSpotForm(
        {
          email: user.email,
          firstname: user.given_name || user.name,
          lastname: user.family_name,
        },
        HubSpotFormsIds.OxAppForm,
      );
      firstMount.current = false;
    }
  }, [user]);

  if (!user) {
    return null;
  }

  return (
    <>
      <Box className={classes.verificationContent}>
        <Typography variant='h5' color='text.primary'>
          Please verify your email
        </Typography>
        <Box mt={2} mb={2} justifyContent='center'>
          <Typography variant='body1' color='text.primary'>
            You're almost there! We sent an email to
          </Typography>
          <Typography
            variant='body1'
            color='text.primary'
            style={{ fontWeight: 600 }}>
            {user.email}
          </Typography>
        </Box>
        <Box mb={2}>
          <Typography variant='body1' color='text.primary'>
            Just click on the link in that email to complete your signup.
          </Typography>
          <Typography variant='body1' color='text.primary'>
            If you don't see it, you may need to check your spam folder.
          </Typography>
        </Box>
        <Box mb={2}>
          <Typography variant='body1' color='text.primary'>
            Still can't find the email?
          </Typography>
          <LoadingButton
            loading={isLoading}
            size='medium'
            variant='contained'
            color='success'
            onClick={onClick}>
            Resend Email
          </LoadingButton>
        </Box>
        {emailVerificationError && (
          <Typography variant='body2' color='error'>
            {emailVerificationError}
          </Typography>
        )}
        <Typography variant='body1' color='text.primary'>
          Need help? <Link href='mailto:support@ox.security'>Contact Us</Link>
        </Typography>
      </Box>
      <Box
        component='img'
        src={oxTheOx}
        alt='page not found'
        maxHeight='80vh'
        ml={10}
      />
    </>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  verificationContent: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    textAlign: 'center',
  },
  iconButton: {
    marginRight: theme.spacing(5),
  },
  appIcon: {
    width: 300,
    marginRight: theme.spacing(),
  },
}));

export default EmailVerificationContainer;
